// Klientské komponenty aplikace desktop: Správce úloh, O systému, Vlastnosti. Shell plochy je v jádru klienta (client/shell).
import { registerComponent } from '@core/views/factory';
import './properties';
import './apppasswords';
import './totp';
import { shareEditor } from '@core/components/share';
import { linkOptionsDialog, createLink, linkUrl, type CreatedLink } from '@core/components/publiclink';
import { core, ApiError } from '@core/api';
import { h, clear } from '@core/dom';
import { t, tk, fmtDateTime, fmtSize, fmtRelative } from '@core/i18n';
import { icon, fileIcon } from '@core/icons';
import { IwTabs } from '@core/components/misc';
import { IwTable } from '@core/components/table';
import { IwCommandBar, type BarItem } from '@core/components/commandbar';
import { renderBarHeader, simpleBar } from '@core/components/chrome';
import type { ColumnDef } from '@core/contract';
import { toast, toastError } from '@core/components/toast';
import { confirm } from '@core/components/dialog';
import type { StorageItem, Shortcut } from '@core/contract';

interface SessionRow {
  id: string;
  kind: string;
  created_at: string;
  last_at: string;
  ip: string;
  ua: string;
  locked: number;
}
interface DeviceRow {
  id: string;
  label: string;
  created_at: string;
  last_at: string;
  ip: string;
}

/**
 * Správce úloh (nový vzhled, vzor Kontakty): záložky Okna · Přihlášení · Zapamatovaná zařízení, v každé tabulka jádra
 * s výběrem; akce nad výběrem v liště záhlaví (Přepnout, Zavřít okno / Ukončit / Zapomenout), Obnovit v ⋯.
 */
registerComponent('desktop-tasks', ({ desc, wm, win }) => {
  const payload = desc.payload as { sessions: SessionRow[]; devices: DeviceRow[]; currentSession: string };
  const tabs = new IwTabs();
  const bar = new IwCommandBar();
  const col = (key: string, label: string, type: ColumnDef['type'] = 'text', extra: Partial<ColumnDef> = {}): ColumnDef => ({ key, label, type, sortable: false, searchable: false, width: null, align: null, hidden: false, mono: false, options: null, ...extra });
  const mkTable = (cols: ColumnDef[], empty: string): IwTable => {
    const tb = new IwTable();
    tb.emptyText = empty;
    tb.setColumns(cols);
    tb.onSelect = () => render();
    return tb;
  };
  const winTable = mkTable([col('title', t('desktop.tasks.window')), col('app', t('desktop.tasks.app'), 'text', { mono: true, width: 220 }), col('who', t('desktop.tasks.identity'), 'text', { width: 160 })], t('desktop.tasks.none'));
  const sesTable = mkTable([col('kind', t('desktop.tasks.kind'), 'text', { width: 170 }), col('created_at', t('desktop.tasks.since'), 'datetime', { width: 150 }), col('last', t('desktop.tasks.last'), 'text', { width: 140 }), col('ip', t('desktop.tasks.ip'), 'text', { mono: true, width: 140 }), col('ua', t('desktop.tasks.browser'))], t('desktop.tasks.none'));
  const devTable = mkTable([col('label', t('desktop.tasks.browser')), col('created_at', t('desktop.tasks.since'), 'datetime', { width: 150 }), col('last', t('desktop.tasks.last'), 'text', { width: 140 }), col('ip', t('desktop.tasks.ip'), 'text', { mono: true, width: 140 })], t('desktop.tasks.none'));
  tabs.setTabs([
    { id: 'windows', label: t('desktop.tasks.windows') },
    { id: 'sessions', label: t('desktop.tasks.sessions') },
    { id: 'devices', label: t('desktop.tasks.devices') },
  ]);
  tabs.panel('windows').append(winTable);
  tabs.panel('sessions').append(sesTable);
  tabs.panel('devices').append(devTable);
  const el = h('div', { class: 'view tasks-view chrome-bar' }, tabs);
  let sessions = payload.sessions;
  let devices = payload.devices;

  const others = () => wm.windows.filter((w) => w !== win);
  const fillWindows = () => winTable.setRows(others().map((w, i) => ({ id: String(i), title: w.titleText(), app: w.app + '/' + w.windowId, who: w.identityLabel ?? '—' })), 'id');
  const fillSessions = () => sesTable.setRows(sessions.map((s) => ({ id: s.id, kind: (s.kind === 'runas' ? t('settings.sessions.runas') : t('settings.sessions.tab')) + (s.id === payload.currentSession ? ' · ' + t('desktop.tasks.this') : ''), created_at: s.created_at, last: fmtRelative(s.last_at), ip: s.ip, ua: s.ua.slice(0, 80) })), 'id');
  const fillDevices = () => devTable.setRows(devices.map((d) => ({ id: d.id, label: d.label, created_at: d.created_at, last: fmtRelative(d.last_at), ip: d.ip })), 'id');

  const reload = async () => {
    try {
      sessions = (await core<{ sessions: SessionRow[] }>('sessions', 'list')).sessions;
      devices = (await core<{ devices: DeviceRow[] }>('devices', 'list')).devices;
      fillSessions();
      fillDevices();
      render();
    } catch (e) {
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    }
  };
  const endSessions = async (ids: string[]) => {
    if (!ids.length || !(await confirm(t('desktop.tasks.end') + '?', { danger: true, okLabel: t('desktop.tasks.end') }))) return;
    for (const id of ids) await core('sessions', 'revoke', { id }).catch((e: ApiError) => toastError(e));
    await reload();
  };
  const forget = async (ids: string[]) => {
    if (!ids.length || !(await confirm(t('core.ui.forget_device_q'), { danger: true }))) return;
    for (const id of ids) await core('devices', 'revoke', { id }).catch((e: ApiError) => toastError(e));
    await reload();
  };

  function items(): BarItem[] {
    const tab = tabs.current();
    const out: BarItem[] = [];
    if (tab === 'windows') {
      const sel = winTable.selection().map((i) => others()[Number(i)]).filter((w): w is NonNullable<typeof w> => !!w);
      out.push({ id: 'switch', label: t('desktop.tasks.switch'), icon: 'window', place: 'primary', disabled: sel.length !== 1, onClick: () => { const w = sel[0]; if (w) { if (w.state === 'minimized') w.restore(); w.focusWindow(); } } });
      out.push({ id: 'close', label: t('desktop.tasks.close'), icon: 'x', place: 'selection', disabled: !sel.length, onClick: () => void Promise.all(sel.map((w) => w.close())).then(() => { fillWindows(); render(); }) });
    } else if (tab === 'sessions') {
      const sel = sesTable.selection().filter((id) => id !== payload.currentSession);
      out.push({ id: 'end', label: t('desktop.tasks.end'), icon: 'log-out', place: 'selection', danger: true, disabled: !sel.length, onClick: () => void endSessions(sel) });
    } else {
      const sel = devTable.selection();
      out.push({ id: 'forget', label: t('desktop.tasks.forget'), icon: 'x', place: 'selection', danger: true, disabled: !sel.length, onClick: () => void forget(sel) });
    }
    out.push({ id: 'refresh', label: t('core.cmd.refresh'), icon: 'refresh', place: 'more', shortcut: 'F5', onClick: () => { fillWindows(); void reload(); } });
    return out;
  }
  function render(): void {
    bar.setItems(items());
    renderBarHeader({ win, wm, bar });
  }
  tabs.onChange = () => render();

  fillWindows();
  fillSessions();
  fillDevices();
  render();
  const prev = wm.onChange;
  wm.onChange = () => {
    prev?.();
    fillWindows();
    render();
  };
  return {
    el,
    refresh: () => { fillWindows(); void reload(); },
    setMode: () => render(),
    getState: () => ({ tab: tabs.current() }),
    setState: (st) => {
      const x = st as { tab?: string } | null;
      if (x?.tab) tabs.select(x.tab);
    },
    destroy: () => {
      wm.onChange = prev;
    },
  };
});

registerComponent('desktop-about', ({ desc, win, wm }) => {
  const p = desc.payload as { system: { name: string; version: string; vendor: { name: string; url: string } }; customer: { code: string; name: string }; php: string; db: string; apps: { id: string; name: string; version: string; system: boolean }[] };
  const diag = () =>
    [
      `${p.system.name} ${p.system.version}`,
      `Zákazník: ${p.customer.name} (${p.customer.code})`,
      `Server: PHP ${p.php}, DB ${p.db}`,
      `Prohlížeč: ${navigator.userAgent}`,
      `Obrazovka: ${window.innerWidth}×${window.innerHeight}, ${window.devicePixelRatio}x`,
      `Aplikace: ${p.apps.map((a) => `${a.id} ${a.version}`).join(', ')}`,
      `Čas: ${new Date().toISOString()}`,
    ].join('\n');
  const copy = h('button', { type: 'button', class: 'btn' }, icon('copy'), t('desktop.about.copy'));
  copy.addEventListener('click', () => void navigator.clipboard.writeText(diag()).then(() => toast(t('desktop.about.copied'), 'ok')));
  const row = (label: string, value: string | Node) => h('div', { class: 'about-row' }, h('span', { class: 'muted' }, label), h('span', null, value));
  const el = h(
    'div',
    { class: 'view about-view' },
    h('div', { class: 'about-head' }, h('div', { class: 'about-logo' }, icon('logo', 'icon big')), h('div', null, h('h2', null, `${p.system.name} ${p.system.version}`), h('a', { href: p.system.vendor.url, target: '_blank', rel: 'noopener' }, p.system.vendor.name))),
    row(t('desktop.about.customer'), `${p.customer.name} (${p.customer.code})`),
    row(t('desktop.about.server'), `PHP ${p.php} · ${p.db}`),
    row(t('desktop.about.browser'), navigator.userAgent.replace(/^Mozilla\/5\.0 /, '').slice(0, 90)),
    h('h3', null, t('desktop.about.apps')),
    h('ul', { class: 'about-apps' }, p.apps.map((a) => h('li', null, h('b', null, tk(a.name)), ' ', h('span', { class: 'mono muted' }, a.version), a.system ? h('span', { class: 'badge' }, 'systém') : null))),
    h('div', { class: 'about-foot' }, copy),
  );
  const sb = simpleBar(win, wm, () => [{ id: 'copy', label: t('desktop.about.copy'), icon: 'copy', place: 'primary', onClick: () => copy.click() }]);
  if (sb.active) el.querySelector('.about-foot')?.remove();
  return { el, setMode: () => sb.refresh() };
});

registerComponent('desktop-share', ({ desc, win }) => {
  const p = desc.payload as { type: string; id: string; title: string | null; people?: boolean; links?: boolean; kind?: string | null };
  if (p.title) win.setTitle(`${t('core.share.title')} – ${p.title}`, 'users');
  const el = h('div', { class: 'view props-view share-dlg' });
  if (p.people !== false) el.append(h('section', { class: 'share-sec' }, h('h3', null, icon('users'), t('core.share.people')), shareEditor(p.type, p.id)));
  if (p.links) el.append(h('section', { class: 'share-sec' }, h('h3', null, icon('link'), t('core.links.dialog_title')), linksBox(p.id, p.title ?? '', p.kind === 'folder')));
  return { el };
});

/** Odkazy pro lidi mimo firmu u položky: platné odkazy (adresa, co jde, platnost, Kopírovat, Zrušit) a Vytvořit odkaz. */
function linksBox(itemId: string, name: string, folder: boolean): HTMLElement {
  const box = h('div', { class: 'share-links' });
  let fresh: string | null = null; // právě vytvořený odkaz – adresa rozbalená
  type L = CreatedLink & { state: string; mine?: boolean; downloads: number; uploads: number; itemId: string };
  const copyBtn = (url: string) => {
    const b = h('button', { type: 'button', class: 'btn sm' }, icon('copy'), t('core.links.copy'));
    b.addEventListener('click', () => void navigator.clipboard?.writeText(url).then(() => toast(t('core.links.copied'), 'ok')));
    return b;
  };
  async function load(): Promise<void> {
    box.replaceChildren(h('div', { class: 'spinner' }));
    let links: L[] = [];
    try {
      links = (await core<{ links: L[] }>('links', 'forItem', { id: itemId })).links.filter((l) => l.state === 'active');
    } catch (e) {
      box.replaceChildren(h('p', { class: 'muted' }, tk((e as ApiError).message)));
      return;
    }
    const rows = links.map((l) => {
      const url = linkUrl(l.path);
      const what = l.kind === 'folder' ? t('core.links.perm_' + l.perm) : t('core.links.perm_read_file');
      const until = l.expiresAt ? t('core.links.until', { date: fmtDateTime(l.expiresAt) }) : t('core.links.forever');
      const other = l.itemId !== itemId ? ' · ' + t('core.links.parent_link', { name: l.name }) : '';
      const revoke = h('button', { type: 'button', class: 'btn ghost sm', title: t('sdileni.cmd.revoke') }, icon('x'));
      revoke.addEventListener('click', async () => {
        if (!(await confirm(t('sdileni.confirm_revoke', { count: 1 }), { danger: true }))) return;
        try { await core('links', 'revoke', { id: l.id }); void load(); } catch (e) { toastError(e); }
      });
      const input = h('input', { class: 'input link-url', type: 'text', value: url, readonly: true }) as HTMLInputElement;
      input.addEventListener('focus', () => input.select());
      return h('div', { class: 'link-row' + (l.id === fresh ? ' fresh' : '') },
        h('div', { class: 'link-row-head' }, icon(l.password ? 'lock' : 'link'), h('span', { class: 'link-row-what' }, what), h('span', { class: 'muted small' }, until + other), h('span', { class: 'spacer' }), revoke),
        h('div', { class: 'link-url-row' }, input, copyBtn(url)));
    });
    const create = h('button', { type: 'button', class: 'btn' + (links.length ? '' : ' primary') }, icon('plus'), t('core.links.create_new'));
    create.addEventListener('click', async () => {
      const o = await linkOptionsDialog([name], folder);
      if (!o) return;
      const link = await createLink(itemId, o, { app: 'soubory' });
      if (!link) return;
      fresh = link.id;
      if (o.password) toast(t('core.links.password_note'), 'info');
      void navigator.clipboard?.writeText(linkUrl(link.path)).then(() => toast(t('core.links.copied'), 'ok')).catch(() => undefined);
      void load();
    });
    box.replaceChildren(...(rows.length ? rows : [h('p', { class: 'muted' }, t('core.links.none'))]), h('div', { class: 'share-links-foot' }, create, h('span', { class: 'muted small' }, t('core.links.where'))));
  }
  void load();
  return box;
}
